import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { Section } from "../components/Section";
import { WorkflowAssociationsTable } from "../components/workflows/WorkflowAssociationsTable";
import { WorkflowDetailForm } from "../components/workflows/WorkflowDetailForm";
import { useTabSearch } from "../hooks";

export function EditWorkflowPage(params: { id: number }) {
  const tabsProps = useTabSearch();

  const {
    data: workflow,
    isPending,
    error,
  } = trpc.admin.workflow.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/workflows" }}>Rules</Breadcrumb>
            <Breadcrumb>{workflow.name}</Breadcrumb>
          </>
        }
        actions={
          <DeleteWorkflowDialog workflow={workflow}>
            <Button variant="destructive">Delete</Button>
          </DeleteWorkflowDialog>
        }
      >
        {workflow.name}
      </Section.Title>
      <Tabs {...tabsProps}>
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
          <Tab id="associations">Associations</Tab>
        </TabsList>
        <TabPanel id="details">
          <WorkflowDetailForm workflow={workflow} />
        </TabPanel>
        <TabPanel id="associations">
          <WorkflowAssociationsTable workflow={workflow} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeleteWorkflowDialog({
  children,
  workflow,
}: {
  children: ReactNode;
  workflow: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteWorkflow = trpc.admin.workflow.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deleteWorkflow.mutate(
      workflow,
      mutationResponse({
        errorTitle: "Error deleting rule",
        successMessage: "Rule deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/workflows" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete rule"
      message="Are you sure you want to delete this rule?"
      onConfirm={onConfirm}
      loading={deleteWorkflow.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
